import React, { useState, useEffect } from 'react';
import { HashRouter, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Dashboard from './pages/Dashboard';
import Inventory from './pages/Inventory';
import Insights from './pages/Insights';
import Planner from './pages/Planner';
import Campaigns from './pages/Campaigns';
import SEO from './pages/SEO';
import SEOMonitor from './pages/SEOMonitor';
import Analytics from './pages/Analytics';
import Settings from './pages/Settings';
import { GoalMode, AudienceType, Environment, User, ConnectionStatus, ConnectionConfig } from './types';
import { checkApiHealth } from './services/apiService';
import { toast, ToastContainer } from './components/Toast';

const defaultConnection = (): ConnectionConfig => ({ connected: false });
const sampleConnection = (): ConnectionConfig => ({ connected: true, apiKey: 'SAMPLE_KEY_ABC_123' });

const App: React.FC = () => {
  const [initStatus, setInitStatus] = useState<'loading' | 'success'>('loading');
  
  const [goalMode, setGoalMode] = useState<GoalMode>(GoalMode.RESEARCH_VISIBILITY);
  const [audienceMode, setAudienceMode] = useState<AudienceType>(AudienceType.GRADUATE_APPLICANTS);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  
  const [environments, setEnvironments] = useState<Environment[]>([]);

  const [activeEnvId, setActiveEnvId] = useState<string>('none');
  const activeEnv = environments.find(e => e.id === activeEnvId) || null;

  // Load environments from database when user logs in
  useEffect(() => {
    if (!user) {
      setEnvironments([]);
      setActiveEnvId('none');
      return;
    }

    const loadEnvironments = async () => {
      try {
        const response = await fetch('/api/environments', {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          setEnvironments(data.environments || []);

          // If no environments, auto-select first one
          if (data.environments && data.environments.length > 0 && activeEnvId === 'none') {
            setActiveEnvId(data.environments[0].id);
          }
        }
      } catch (error) {
        console.error('Failed to load environments:', error);
      }
    };

    loadEnvironments();
  }, [user]); // Re-load when user changes

  useEffect(() => {
    let mounted = true;

    const initializeApp = async () => {
      // Create a hard promise that resolves no matter what after 3s for a snappier feel
      const timeoutPromise = new Promise(resolve => setTimeout(resolve, 3000));

      try {
        // Check authentication status
        const authResponse = await fetch('/api/auth/me', {
          credentials: 'include' // Include cookies
        });
        const authData = await authResponse.json();

        if (mounted && authData.authenticated && authData.user) {
          setUser(authData.user);
        }

        // Race the health check against the timeout. Even if it fails, we want the app to load.
        await Promise.race([
          checkApiHealth(),
          timeoutPromise
        ]);
      } catch (err) {
        console.warn("System: Initialization delay encountered. Bootstrapping with local nodes.");
      } finally {
        if (mounted) {
          setInitStatus('success');
        }
      }
    };

    initializeApp();
    return () => { mounted = false; };
  }, []);

  const handleLogin = () => {
    setUser({
      name: 'Demo User',
      email: 'demo@example.com',
      role: 'Admin'
    });
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include'
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
    }
  };

  const addEnvironment = async (name: string) => {
    try {
      const response = await fetch('/api/environments', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });

      if (response.ok) {
        const newEnv = await response.json();
        setEnvironments([...environments, newEnv]);
        setActiveEnvId(newEnv.id);
      } else {
        console.error('Failed to create environment');
        toast.error('Failed to create environment. Please try again.');
      }
    } catch (error) {
      console.error('Error creating environment:', error);
      toast.error('Failed to create environment. Please try again.');
    }
  };

  const removeEnvironment = async (id: string) => {
    const envName = environments.find(e => e.id === id)?.name;
    if (!confirm(`Delete environment "${envName}"?`)) return;

    try {
      const response = await fetch(`/api/environments?id=${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setEnvironments(environments.filter(e => e.id !== id));
        if (activeEnvId === id) setActiveEnvId('none');
      } else {
        console.error('Failed to delete environment');
        toast.error('Failed to delete environment. Please try again.');
      }
    } catch (error) {
      console.error('Error deleting environment:', error);
      toast.error('Failed to delete environment. Please try again.');
    }
  };

  const renameEnvironment = async (id: string, newName: string) => {
    try {
      const response = await fetch('/api/environments', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, name: newName })
      });

      if (response.ok) {
        setEnvironments(envs => envs.map(env =>
          env.id === id ? { ...env, name: newName } : env
        ));
      } else {
        toast.error('Failed to rename environment.');
      }
    } catch (error) {
      console.error('Error renaming environment:', error);
      toast.error('Failed to rename environment.');
    }
  };

  const updateConnection = async (envId: string, key: keyof ConnectionStatus, config: ConnectionConfig) => {
    try {
      const response = await fetch('/api/connections', {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          environmentId: envId,
          connectionType: key,
          config
        })
      });

      if (response.ok) {
        // Update local state
        setEnvironments(envs => envs.map(env => {
          if (env.id === envId) {
            return { ...env, connections: { ...env.connections, [key]: config } };
          }
          return env;
        }));
      } else {
        console.error('Failed to update connection');
        toast.error('Failed to save connection. Please try again.');
      }
    } catch (error) {
      console.error('Error updating connection:', error);
      toast.error('Failed to save connection. Please try again.');
    }
  };

  if (initStatus === 'loading') {
    return (
      <div className="fixed inset-0 bg-slate-900 flex flex-col items-center justify-center text-white p-6 z-[9999]">
        <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-2xl mb-8 animate-pulse">
          <span className="text-white font-black text-xl">SI</span>
        </div>
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-black tracking-tight">Site Intelligence</h1>
          <div className="flex items-center justify-center gap-3">
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
            <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
          </div>
          <p className="text-white/60 text-[10px] font-bold uppercase tracking-[0.4em]">Initializing</p>
        </div>
      </div>
    );
  }

  return (
    <HashRouter>
      <ToastContainer />
      <div className="flex h-screen w-full bg-slate-50 transition-all duration-300 overflow-hidden">
        <Sidebar
          isCollapsed={isSidebarCollapsed}
          setIsCollapsed={setIsSidebarCollapsed}
          environments={environments}
          activeEnvId={activeEnvId}
          setActiveEnvId={setActiveEnvId}
          addEnvironment={addEnvironment}
          removeEnvironment={removeEnvironment}
          renameEnvironment={renameEnvironment}
        />
        
        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
          <Header
            activeEnv={activeEnv}
            user={user}
            onLogin={handleLogin}
            onLogout={handleLogout}
          />
          
          <main className="flex-1 p-4 md:p-8 overflow-y-auto bg-slate-50/50">
            <div className="max-w-7xl mx-auto">
              <Routes>
                <Route path="/" element={<Dashboard goalMode={goalMode} audienceMode={audienceMode} activeEnv={activeEnv} />} />
                <Route path="/inventory" element={<Inventory goalMode={goalMode} activeEnv={activeEnv} />} />
                <Route path="/insights" element={<Insights goalMode={goalMode} audienceMode={audienceMode} activeEnv={activeEnv} />} />
                <Route path="/planner" element={<Planner goalMode={goalMode} audienceMode={audienceMode} activeEnv={activeEnv} />} />
                <Route path="/campaigns" element={<Campaigns goalMode={goalMode} activeEnv={activeEnv} />} />
                <Route path="/seo-monitor" element={<SEOMonitor activeEnv={activeEnv} />} />
                <Route path="/analytics" element={<Analytics activeEnv={activeEnv} />} />
                <Route path="/seo" element={<SEO activeEnv={activeEnv} />} />
                <Route path="/settings" element={<Settings activeEnv={activeEnv} onUpdateConnection={updateConnection} />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </HashRouter>
  );
};

export default App;